import {Component, Inject} from '@angular/core';
import {AutoUnsubscribe} from '@secca/shared/decorators/auto-unsubscribe';
import {IKeyboardEnabled} from '@secca/shared/interfaces/keyboard-enabled';
import {DialogBoundryService} from '@secca/core/services/dialog-boundry.service';
import {DialogHelperUtilService} from '@secca/core/services/dialog-helper-util.service';
import {ShortcutService} from '@secca/core/services/shortcut.service';
import {DialogKeyboardEnabledDirective} from '@secca/shared/directives/dialog-keyboard-enabled.directive';
import {asapScheduler, Observable, scheduled, Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {InputTypePattern} from '@secca/shared/models/enums';
import {RecoveryService} from '@secca/core/services/recovery.service';
import {Recovery} from '@secca/shared/models/recovery';
import { HttpErrorResponse } from '@angular/common/http';
import {CaseService} from '@secca/core/services/case.service';
import {Case} from '@secca/shared/models/case';
import {TaskService} from '@secca/core/services/task.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {Task} from '@secca/shared/models/task';
import {TaskMessageModalMovePaymentData} from './task-message-modal-move-payment-data';
import {RecoveryPaymentService} from '@secca/core/services/recovery/recovery-payment.service';
import {RecoveryPayment} from '@secca/shared/models/recoveryPayment';

@Component({
    selector: 'app-task-message-modal-move-payment',
    templateUrl: './task-message-modal-move-payment.component.html',
    styleUrls: ['./task-message-modal-move-payment.component.scss'],
    standalone: false
})
@AutoUnsubscribe
export class TaskMessageModalMovePaymentComponent implements IKeyboardEnabled {


  constructor(private shortcutService: ShortcutService,
              private dialogHelperUtilService: DialogHelperUtilService,
              private recoveryService: RecoveryService,
              private recoveryPaymentService: RecoveryPaymentService,
              private caseService: CaseService,
              private taskService: TaskService,
              public dialogBoundaryService: DialogBoundryService,
              public dialogRef: MatDialogRef<TaskMessageModalMovePaymentComponent>,
              @Inject(MAT_DIALOG_DATA) data: TaskMessageModalMovePaymentData) {
    this.data = data;
    this.shortcutService.enableShortCuts();

    if (this.data.isUnhandledRecoveryPaymentTask === false) {
      this.taskService.getTask(data.originTaskId).subscribe({
        next: (task: Task) => {
          this.recoveryPaymentService.getRecoveryPayment(task.recoveryPaymentId).subscribe({
            next: (recoveryPayment: RecoveryPayment) => {
              this.originPayment = recoveryPayment;
            },
            error: (response: HttpErrorResponse) => {
              console.log('Unable to fetch recovery application with id ' + task.recoveryId);
            }
          });
        },
        error: (response: HttpErrorResponse) => {
          console.log('Unable to fetch task with id ' + data.originTaskId);
        }
      });
    }

    this.$inputSubscription = scheduled(this.recoveryApplicationNumberChangeEvent, asapScheduler).pipe(
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe((recoveryNumberString: string) => {
      const validRecoveryApplicationNumber = recoveryNumberString?.replace(this.allNonNumericExpression, '');

      if (validRecoveryApplicationNumber !== '' && validRecoveryApplicationNumber !== this.lastValidRecoveryApplicationNumber) {
        this.lastValidRecoveryApplicationNumber = validRecoveryApplicationNumber;
        const recoveryNumber = parseInt(recoveryNumberString, 10);

        if (isNaN(recoveryNumber) === false) {
          const application: Observable<Recovery> = recoveryService.getRecoveryById(recoveryNumber);

          application.subscribe({
            next: (theApplication: Recovery) => {
              if (this.data.isUnhandledRecoveryPaymentTask === false) {
                if (theApplication.recoveryCurrency !== this.originPayment.getInvoiceAmountCurrency()) {
                  this.shouldShowLookup = true;
                  this.isAbleToMove = false;
                  this.isLookupSuccessful = true;
                  this.caseBehindRecoveryApplication = null;
                  return;
                }
              }

              this.shouldShowLookup = true;
              const theCase = caseService.getCase(theApplication.caseId.toString());

              theCase.subscribe({
                next: (aCase: Case) => {
                  if (aCase?.caseNumber !== null) {
                    this.isLookupSuccessful = true;
                    this.caseBehindRecoveryApplication = aCase;
                    this.isAbleToMove = true;
                  } else {
                    this.isLookupSuccessful = false;
                    this.isAbleToMove = false;
                  }
                }
                , error: (anError: HttpErrorResponse) => {
                  this.caseBehindRecoveryApplication = null;
                  this.isLookupSuccessful = false;
                  this.isAbleToMove = false;
                }
              });
            }
            , error: (anError: HttpErrorResponse) => {
              this.isLookupSuccessful = false;
              this.shouldShowLookup = true;
              this.isAbleToMove = false;
              this.caseBehindRecoveryApplication = null;
            }
          });
        }
      } else {
        if (validRecoveryApplicationNumber === '' ) {
          this.shouldShowLookup = false;
          this.caseBehindRecoveryApplication = null;
        }
      }
    });
  }

  get enabledKeys(): string[] {
    return [DialogKeyboardEnabledDirective.ESCAPE_KEY];
  }

  data: TaskMessageModalMovePaymentData;
  recoveryApplicationNumber: string;
  isMoveInProgress = false;
  isAbleToMove = false;
  shouldShowLookup = false;
  isLookupSuccessful = true;
  caseBehindRecoveryApplication: Case | null = null;
  originPayment: RecoveryPayment | null = null;
  private lastValidRecoveryApplicationNumber: string = undefined;
  private allNonNumericExpression: RegExp = /[^0-9]/g;
  private recoveryApplicationNumberChangeEvent = new Subject();
  private $inputSubscription: Subscription;

  protected readonly InputTypePattern = InputTypePattern;

  recoveryApplicationNumberChanged(event: string) {
    this.recoveryApplicationNumberChangeEvent.next(event);
  }

  moveButtonClicked() {
    this.isMoveInProgress = true;

    if (this.data.isUnhandledRecoveryPaymentTask) {
      this.taskService.moveUnhandledRecoveryPaymentToRecoveryApplication(this.data.originTaskId, +this.recoveryApplicationNumber)
        .subscribe({
          next: (task: Task) => {
            this.close('recoveryPaymentMoved');
          },
          error: (response: HttpErrorResponse) => {
            this.close(this.recoveryApplicationNumber);
          }
        });
    } else {
      this.taskService.moveRecoveryPaymentToRecoveryApplication(this.data.originTaskId, +this.recoveryApplicationNumber)
        .subscribe({
          next: (task: Task) => {
            this.close('recoveryPaymentMoved');
          },
          error: (response: HttpErrorResponse) => {
            this.close(this.recoveryApplicationNumber);
          }
        });
    }
  }

  lookupMessage(): string {
    if (this.data.isUnhandledRecoveryPaymentTask) {
      return this.isLookupSuccessful ? 'dialog-message-modal-move-recovery-application-found' : 'dialog-message-modal-move-recovery-application-not-found';
    } else {
      if (this.isLookupSuccessful && this.isAbleToMove === false) {
        return 'dialog-message-modal-move-recovery-application-currency-mismatch';
      } else {
        return this.isLookupSuccessful ? 'dialog-message-modal-move-recovery-application-found' : 'dialog-message-modal-move-recovery-application-not-found';
      }
    }
  }

  close(value: string | number | undefined): void {
    this.isMoveInProgress = false;
    this.$inputSubscription.unsubscribe();
    this.dialogRef.close(value);
  }

  keyboardClose(): void {
    this.dialogRef.close();
  }
}
